import React from "react";
import type { Question } from "../types/question.types";

const LIKERT_OPTIONS = [
  { label: "Strongly Disagree", value: 1 },
  { label: "Disagree", value: 2 },
  { label: "Neutral", value: 3 },
  { label: "Agree", value: 4 },
  { label: "Strongly Agree", value: 5 },
] as const;

const LIKERT_INSTRUCTION =
  "Read each statement and decide how much you agree or disagree with that statement.";

export interface LikertProps {
  question: Extract<Question, { layout: "likert" }>;
  value?: number;
  onChange?: (answerKey: string, value: number) => void;
}

const Likert: React.FC<LikertProps> = ({ question, value, onChange }) => {
  const { title, answer_key } = question.attributes;
  const name = question.key;
  const storeKey = answer_key;

  return (
    <div className="flex flex-col w-full min-w-0 py-4 sm:py-6 md:py-8 min-h-0">
      <p
        className="text-[#26295e] font-primary font-semibold text-base sm:text-lg md:text-xl mb-6 sm:mb-8 md:mb-[4rem] text-center px-2"
        style={{ fontFamily: "var(--font-primary)" }}
      >
        {LIKERT_INSTRUCTION}
      </p>
      <fieldset className="border-0 p-0 m-0 w-full min-w-0">
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 p-3 sm:p-4 lg:p-5 rounded border border-[#e5e7eb] w-full min-w-0">
          {/* Statement - left on large screens */}
          <div
            className="w-full lg:w-auto lg:min-w-[200px] lg:max-w-[360px] lg:shrink-0 lg:flex lg:items-center px-1 lg:px-4 lg:py-4"
            style={{ fontFamily: "var(--font-primary)" }}
          >
            <p className="text-[#26295e] font-primary font-medium text-sm sm:text-base m-0">
              {title}
            </p>
          </div>

          {/* Options - right on large screens, label above radio */}
          <div className="flex flex-col gap-2 w-full lg:flex-1 lg:min-w-0 lg:flex-row lg:flex-nowrap lg:items-stretch lg:justify-between lg:gap-4 lg:py-2">
            {LIKERT_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex min-h-[44px] cursor-pointer flex-row items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-[#f8f9fc] has-[:checked]:bg-[#eef0f9] lg:min-w-[5.5rem] lg:flex-1 lg:flex-col lg:items-center lg:justify-start lg:gap-2 lg:rounded-lg lg:px-3 lg:py-3"
              >
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => onChange?.(storeKey, option.value)}
                  className="h-4 w-4 shrink-0 border-[#7d86a9] text-[#3b519c] focus:ring-[#3b519c] lg:order-2"
                />
                <span className="text-sm font-medium text-[#26295e] lg:order-1 lg:block lg:text-center lg:leading-tight lg:min-h-[2.5rem]">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default Likert;
